import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ProductCard from "@/components/site/ProductCard";
import { useStore } from "@/lib/store";
import { translations, CATEGORY_LABELS } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CATEGORIES = ["all", "jerseys_countries", "jerseys_clubs", "jerseys_neutral", "cleats", "shoes", "accessories"];
const PAGE_SIZE = 24;

export default function Shop() {
  const { lang } = useStore();
  const t = translations[lang];
  const [params, setParams] = useSearchParams();
  const initial = params.get("category") || "all";
  const [category, setCategory] = useState(initial);
  const [page, setPage] = useState(parseInt(params.get("page") || "1", 10));
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/products`, {
        params: {
          ...(category && category !== "all" ? { category } : {}),
          page,
          limit: PAGE_SIZE,
        },
      })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    const next = new URLSearchParams();
    if (category !== "all") next.set("category", category);
    if (page > 1) next.set("page", String(page));
    setParams(next, { replace: true });
    window.scrollTo({ top: 200, behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, page]);

  const switchCategory = (c) => {
    setCategory(c);
    setPage(1);
  };

  const goToPage = (p) => {
    if (p >= 1 && p <= data.total_pages) setPage(p);
  };

  // Pagination range
  const pages = [];
  const tp = data.total_pages || 1;
  const around = 1;
  for (let i = 1; i <= tp; i++) {
    if (i === 1 || i === tp || (i >= page - around && i <= page + around)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-14" data-testid="shop-page">
      <p
        className="text-xs tracking-[0.5em] text-yellow-400/80 mb-4"
        style={{ fontFamily: '"JetBrains Mono", monospace' }}
      >
        BRAF_ZAK · {lang === "fr" ? "CATALOGUE" : "CATALOG"}
      </p>
      <h1
        className="text-5xl sm:text-6xl font-black text-white"
        style={{ fontFamily: '"Bebas Neue", system-ui' }}
      >
        {t.shop_title}
      </h1>
      <p className="text-white/60 mt-3 mb-2">{t.shop_subtitle}</p>
      <p className="text-xs text-yellow-300/70 tracking-widest uppercase mb-8" data-testid="shop-count">
        {data.total} {lang === "fr" ? "produits disponibles" : "products available"}
      </p>

      <div
        className="flex flex-wrap gap-2 mb-10 border-b border-yellow-600/15 pb-6"
        data-testid="shop-filters"
      >
        {CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c}`}
            onClick={() => switchCategory(c)}
            className={`text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full transition-all ${
              category === c
                ? "text-black"
                : "text-yellow-200/80 border border-yellow-500/30 hover:border-yellow-400"
            }`}
            style={
              category === c
                ? {
                    background: "linear-gradient(180deg, #F4D77A, #D4AF37)",
                    boxShadow: "0 6px 16px -4px rgba(212,175,55,0.5)",
                  }
                : undefined
            }
          >
            {CATEGORY_LABELS[c][lang]}
          </button>
        ))}
      </div>

      {loading && data.items.length === 0 ? (
        <p className="text-white/40 text-center py-20 tracking-widest">···</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {data.items.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>

          {tp > 1 && (
            <nav
              className="mt-14 flex items-center justify-center gap-2"
              data-testid="pagination"
            >
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                data-testid="page-prev"
                className="w-10 h-10 rounded-full border border-yellow-500/30 text-yellow-300 flex items-center justify-center disabled:opacity-30 hover:border-yellow-400"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {pages.map((p, idx) =>
                p === "…" ? (
                  <span key={`e${idx}`} className="text-white/40 px-2">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => goToPage(p)}
                    data-testid={`page-${p}`}
                    className={`min-w-[2.5rem] h-10 rounded-full text-sm font-bold transition-all ${
                      p === page
                        ? "text-black"
                        : "text-white/70 border border-yellow-500/20 hover:border-yellow-400"
                    }`}
                    style={
                      p === page
                        ? {
                            background: "linear-gradient(180deg, #F4D77A, #D4AF37)",
                            boxShadow: "0 6px 16px -4px rgba(212,175,55,0.5)",
                          }
                        : undefined
                    }
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page === tp}
                data-testid="page-next"
                className="w-10 h-10 rounded-full border border-yellow-500/30 text-yellow-300 flex items-center justify-center disabled:opacity-30 hover:border-yellow-400"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
